// --- src/data/m3/Unit4.js ---

export const M3_UNIT_4 = {
  id: "m3-u4-sports-vocabulary",
  title: "Unit 4: Sports & Activities",
  topic: "Sports Vocabulary, Go vs Play, Adjectives",
  iconType: "trophy",
  grammarImage: "/m3-u4-sports.jpg",
  grammarText: "Learn sports vocabulary, when to use GO (swimming, running) vs PLAY (soccer, tennis), and adjectives to describe sports (exciting, boring, dangerous).",
  sets: [

    // ===================================================================
    // SET 1: SPORTS NAMES BASICS (A1)
    // ===================================================================
    {
      id: "m3-u4-set1",
      title: "Set 1: Sports Names ⚽",
      context: `*Lunes por la mañana - Centro deportivo de Toronto*

Miguel es instructor deportivo. Hoy presenta deportes básicos a nuevos clientes.
"¿Conocen estos deportes? Soccer, basketball, tennis..." pregunta mientras señala fotos en la pared.
Los clientes escuchan con atención y toman notas.

*Monday morning - Toronto Sports Center*

Miguel is a sports instructor. Today he presents basic sports to new clients.
"Do you know these sports? Soccer, basketball, tennis..." he asks while pointing at photos on the wall.
The clients listen carefully and take notes.`,
      
      questions: [
        {
          type: "match",
          q: "Match the sport to its description.",
          pairs: [
            { left: "Football ⚽", right: "You kick a ball on a field" },
            { left: "Swimming 🏊", right: "You do this in a pool" },
            { left: "Tennis 🎾", right: "You use a racket and a ball" }
          ],
          a: "completed",
          tip: "Match the action to the sport!",
          successTip: "Soccer = fútbol! Remember: Football (UK) = Soccer (US)! ⚽"
        },
        {
          type: "mc",
          q: "Miguel asks: 'Which sport do you play in a pool?'",
          options: ["Swimming", "Basketball", "Tennis"],
          a: "Swimming",
          tip: "Pool = water = swimming! 🏊"
        },
        {
          type: "input",
          q: "Miguel explains: 'You play ___ on a court with a racket.'",
          a: "tennis",
          alts: [],
          tip: "court es la cancha de tenis",
          successTip: "court = tennis, rink = icehockey, field = soccer! Cada uno tiene su campo🎾"
        },
        {
          type: "match",
          q: "Match more sports to descriptions.",
          pairs: [
            { left: "Basketball 🏀", right: "You throw a ball into a hoop" },
            { left: "Running 🏃", right: "You need special shoes" },
            { left: "Cycling 🚴", right: "You ride a bicycle" }
          ],
          a: "completed",
          successTip: "Basketball = hoop (aro). Running = track (pista). Cycling = bicycle! 🏀🏃🚴"
        },
        {
          type: "mc",
          q: "Client asks: 'Which sport uses a bat?' ⚾",
          options: ["Baseball", "Swimming", "Soccer"],
          a: "Baseball",
          tip: "Bat = palo → Baseball! ⚾"
        },
        {
          type: "input",
          q: "Miguel: 'You ride a bicycle for this sport: ___'",
          a: "cycling",
          alts: ["biking"],
          tip: "Bicycle = ?",
          successTip: "Cycling! Bicycle sport! 🚴"
        },
        {
          type: "match",
          q: "Match winter sports.",
          pairs: [
            { left: "Skiing ⛷️", right: "You slide down snow on skis" },
            { left: "Snowboarding 🏂", right: "Like skiing but with one board" },
            { left: "Ice hockey 🏒", right: "Hockey on ice" }
          ],
          a: "completed",
          successTip: "Winter sports need COLD! Skiing/snowboarding = mountains. Ice hockey = frozen rink! ⛷️🏂"
        },
        {
          type: "mc",
          q: "Miguel shows photos: 'Which sport do you do on snow?' ⛷️",
          options: ["Skiing", "Swimming", "Baseball"],
          a: "Skiing",
          tip: "Snow = skiing/snowboarding! ⛷️"
        },
        {
          type: "input",
          q: "Miguel: 'You surf on waves in the ocean: ___'",
          a: "surfing",
          alts: [],
          tip: "Ocean + waves + board = ?",
          successTip: "waves = olas! Let's ride the waves! 🏄"
        },
        {
          type: "mc",
          q: "Client asks: 'Which sport uses a ball and a hoop?' 🏀",
          options: ["Basketball", "Golf", "Skiing"],
          a: "Basketball",
          tip: "Hoop = aro → Basketball! 🏀",
          successTip: "Basketball! Ball + hoop (aro) = basketball! You throw the ball INTO the hoop! 🏀"
        }
      ]
    },

    // ===================================================================
    // SET 2: GO vs PLAY (A2)
    // ===================================================================
    {
      id: "m3-u4-set2",
      title: "Set 2: GO vs PLAY",
      context: `*Martes por la tarde - Salón de clases del centro*

Miguel enseña la diferencia entre GO y PLAY.
"ir a nadar, ir a correr" escribe en la pizarra.
"Pero jugar fútbol, jugar tenis" añade. "¿Ven la diferencia?"
Los estudiantes asienten y practican.

*Tuesday afternoon - Center classroom*

Miguel teaches the difference between GO and PLAY.
"GO swimming, GO running" he writes on the board.
"But PLAY soccer, PLAY tennis" he adds. "Do you see the difference?"
Students nod and practice.`,
      
      questions: [
        {
          type: "match",
          q: "Match the phrases.",
          pairs: [
            { left: "I never go", right: "swimming" },
            { left: "My friend always", right: "plays soccer" },
            { left: "My mum usually", right: "goes running" }
          ],
          a: "completed",
          successTip: "mum = mother, mum = UK!, mom = US! 🎯"
        },
        {
          type: "mc",
          q: "Miguel asks: 'I ___ swimming every morning.'",
          options: ["go", "play"],
          a: "go",
          tip: "Swimming = GO + -ing form!",
          successTip: "Usa siempre GO + -ing form!💧"
        },
        {
          type: "input",
          q: "Student answers: 'She ___ tennis on weekends.'",
          a: "plays",
          alts: ["play"],
          tip: "Tennis (ball sport) = PLAY!",
          successTip: "PLAYS tennis! PLAY + sports with balls/teams! 🎾"
        },
        {
          type: "match",
          q: "Match the correct verb form.",
          pairs: [
            { left: "My student ___ skiing", right: "goes" },
            { left: "We ___ basketball", right: "play" },
            { left: "They ___ surfing", right: "go" }
          ],
          a: "completed",
          tip: "GO for individual activities (-ing), PLAY for team/ball games!",
          successTip: "Pattern! GO + -ing = individual activities. PLAY + noun = team/ball sports! 📝"
        },
        {
          type: "order",
          q: "Traduce: 'Nado los domingos.'",
          words: ["I", "go", "swimming", "on", "Sundays."],
          a: "I go swimming on Sundays.",
          extra: ["play", "tennis", "running"],
          tip: "GO swimming = subject + go + -ing."
        },
        {
          type: "mc",
          q: "Miguel: 'They ___ baseball after school.'",
          options: ["play", "go"],
          a: "play",
          tip: "Baseball = team sport with a ball → PLAY! ⚾"
        },
        {
          type: "input",
          q: "Student: 'We ___ cycling in the park.'",
          a: "go",
          alts: [],
          tip: "Cycling = -ing form = GO!",
          successTip: "GO cycling! Individual activity! 🚴"
        },
        {
          type: "order",
          q: "Traduce: 'Ella juega tenis dos veces por semana.'",
          words: ["She", "plays", "tennis", "twice", "a", "week."],
          a: "She plays tennis twice a week.",
          extra: ["goes", "swimming", "go"],
          tip: "PLAYS tennis = subject + play(s) + sport."
        },
        {
          type: "mc",
          q: "Miguel explains: 'He ___ running every evening.'",
          options: ["goes", "plays","go"],
          a: "goes",
          tip: "Running = GO + -ing! 🏃"
        },
        {
          type: "input",
          q: "Student asks: 'I ___ golf on Saturdays. GO or PLAY?'",
          a: "play",
          alts: [],
          tip: "Golf = PLAY (sport with a ball)! ⛳",
          successTip: "PLAY golf! Even though it's individual, it's PLAY! Exception! ⛳"
        }
      ]
    },

    // ===================================================================
    // SET 3: SPORTS EQUIPMENT (A2)
    // ===================================================================
    {
      id: "m3-u4-set3",
      title: "Set 3: Equipment ⚾🎾",
      context: `*Miércoles por la mañana - Almacén de equipos*

Miguel organiza el equipo deportivo del centro.
"Aquí están las raquetas para tenis" dice mientras las cuenta.
"Los bates de béisbol están allí. Los clubes de golf aquí."
Todo debe estar ordenado para los clientes.

*Wednesday morning - Equipment storage*

Miguel organizes the sports equipment at the center.
"Here are the rackets for tennis" he says while counting them.
"The baseball bats are there. The golf clubs here."
Everything must be organized for clients.`,
      
      questions: [
        {
          type: "match",
          q: "Match the sport to its equipment.",
          pairs: [
            { left: "Tennis 🎾", right: "Racket" },
            { left: "Baseball ⚾", right: "Bat" },
            { left: "Golf ⛳", right: "Club" }
          ],
          a: "completed",
          successTip: "Racket, bat, club - all for hitting balls! Different sports! 🏏"
        },
        {
          type: "mc",
          q: "Miguel asks: 'What do you need to play tennis?' 🎾",
          options: ["A racket", "A bat", "A club"],
          a: "A racket",
          tip: "Tennis = racket! 🎾"
        },
        {
          type: "input",
          q: "Miguel: 'In baseball, you hit the ball with a ___'",
          a: "bat",
          alts: [],
          tip: "Baseball = bat (palo)!",
          successTip: "Bat! Baseball bat! ⚾"
        },
        {
          type: "match",
          q: "Match equipment to sports.",
          pairs: [
            { left: "Surfboard 🏄", right: "Surfing" },
            { left: "Skis ⛷️", right: "Skiing" },
            { left: "Skateboard 🛹", right: "Skateboarding" }
          ],
          a: "completed",
          tip: "Board sports! All use boards! 🏄⛷️🛹",
          successTip: "BOARD sports! Surfboard (ocean), skis (snow), skateboard (street)! 🛹"
        },
        {
          type: "mc",
          q: "Client asks: 'What do you need for golf?' ⛳",
          options: ["A club and ball", "A racket", "A bat"],
          a: "A club and ball",
          tip: "Golf club = palo de golf! ⛳",
          successTip:"Golf club = palo de golf! ⛳"
        },
        {
          type: "input",
          q: "Miguel: 'You ride on waves with a ___'",
          a: "surfboard",
          alts: [],
          tip: "Surfing needs a surfboard! 🏄",
          successTip: "Surfboard! Long board for riding ocean waves! 🏄🌊"
        },
        {
          type: "match",
          q: "Match the item to the sport.",
          pairs: [
            { left: "Ball 🏀", right: "Basketball, soccer, tennis" },
            { left: "Gloves 🥊", right: "Boxing" },
            { left: "Helmet ⛑️", right: "Cycling, hockey, skateboarding" }
          ],
          a: "completed",
          tip: "Some equipment is for safety!",
          successTip: "Safety first! Helmets for dangerous sports! ⛑️"
        },
        {
          type: "mc",
          q: "Miguel: 'In boxing, you wear ___'",
          options: ["Gloves", "Skis", "A racket"],
          a: "Gloves",
          tip: "Boxing gloves = guantes de boxeo! 🥊"
        },
        {
          type: "input",
          q: "Miguel checks: 'For skiing, you need ___'",
          a: "skis",
          alts: [],
          tip: "Skiing = skis (esquís)!",
          successTip: "Skis! For sliding on snow! ⛷️"
        },
        {
          type: "mc",
          q: "Client asks: 'What sport uses a small white ball and clubs?' ⛳",
          options: ["Golf", "Tennis", "Baseball"],
          a: "Golf",
          tip: "Small white ball + clubs = golf! ⛳",
          successTip: "Golf! Small white ball + clubs + 18 holes! ⛳"
        }
      ]
    },

    // ===================================================================
    // SET 4: SPORTS ADJECTIVES (A2)
    // ===================================================================
    {
      id: "m3-u4-set4",
      title: "Set 4: Describing Sports",
      context: `*Jueves por la tarde - Clase de vocabulario*

Miguel enseña adjetivos para describir deportes.
"El surf es emocionante" dice mostrando un video.
"El golf puede ser aburrido para algunos" admite con una sonrisa.
"¿Y el paracaidismo? ¡Muy peligroso!"

*Thursday afternoon - Vocabulary class*

Miguel teaches adjectives to describe sports.
"Surfing is exciting" he says showing a video.
"Golf can be boring for some people" he admits with a smile.
"And skydiving? Very dangerous!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the adjective to its meaning.",
          pairs: [
            { left: "Emocionante 🎢", right: "Exciting / Thrilling" },
            { left: "Aburrido 😴", right: "Boring / Not interesting" },
            { left: "Peligroso ⚠️", right: "Dangerous / Risky" }
          ],
          a: "completed",
          successTip: "Exciting = love it! Boring = hate it! Dangerous = careful! ⚠️"
        },
        {
          type: "mc",
          q: "Miguel: 'If something is very interesting and fun, it's ___'",
          options: ["exciting", "boring", "tiring"],
          a: "exciting",
          tip: "tiring = agotador! 🎢"
        },
        {
          type: "input",
          q: "Student: 'Golf is ___ for some people.'",
          a: "boring",
          alts: ["not exciting","exciting","interesting","fun","tiring","dangerous","expensive","slow"],
          tip: "Not exciting = boring!",
          successTip: "Boring! Not everyone likes slow sports! 😴"
        },
        {
          type: "match",
          q: "Match more adjectives.",
          pairs: [
            { left: "Cansado 😮‍💨", right: "Tiring / Makes you tired" },
            { left: "Facil 😊", right: "Easy / Simple" },
            { left: "Dificil 😰", right: "Difficult / Hard" }
          ],
          a: "completed",
          tip: "How the sport feels when you do it!",
          successTip: "These describe how YOU FEEL! Tiring = makes you tired. Easy = simple for you! 💪"
        },
        {
          type: "mc",
          q: "Miguel: 'Running a marathon is very ___, specially for beginners.'",
          options: ["tiring", "easy", "dangerous"],
          a: "tiring",
          tip: "Marathon = long = makes you tired! 😮‍💨"
        },
        {
          type: "input",
          q: "Student: 'Skateboarding can be ___ because you can fall.'",
          a: "dangerous",
          alts: ["hazardous","risky","slow","danger"],
          tip: "Can fall = risky = dangerous!",
          successTip: "Dangerous! Falling = danger! ⚠️🛹"
        },
        {
          type: "match",
          q: "Match opposites.",
          pairs: [
            { left: "Exciting", right: "Boring" },
            { left: "Easy", right: "Difficult" },
            { left: "Safe", right: "Dangerous" }
          ],
          a: "completed",
          tip: "Learn opposite pairs!",
          successTip: "Learn opposites! They help you describe better! 🔄"
        },
        {
          type: "mc",
          q: "Miguel: 'Swimming is ___ if you have a good trainer.'",
          options: ["easy", "difficult", "dangerous"],
          a: "easy",
          tip: "Beginners = easy! 😊"
        },
        {
          type: "input",
          q: "Student: 'Rock climbing is very ___'",
          a: "difficult",
          alts: ["hard", "dangerous","risky","complicated"],
          tip: "Climbing rocks = not easy! 🧗",
          successTip: "Difficult OR hard OR dangerous! All work for rock climbing! 🧗"
        },
        {
          type: "mc",
          q: "Miguel: 'If a sport makes you very happy and excited, it's ___'",
          options: ["fun", "boring", "tiring"],
          a: "fun",
          tip: "Happy + excited = fun!",
          successTip: "Fun! The best word for enjoying sports! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 5: READING - SIMPLE OPINIONS (A2)
    // ===================================================================
    {
      id: "m3-u4-set5",
      title: "Set 5: Reading Opinions",
      context: `*Viernes por la mañana - Leyendo comentarios*

Miguel lee las opiniones de clientes sobre deportes.
"A Sarah le encanta la natación. Le parece relajante."
"Pero Tom dice que el baloncesto es aburrido."
Miguel anota cada opinión para mejorar las clases.

*Friday morning - Reading feedback*

Miguel reads clients' opinions about sports.
"Sarah loves swimming. She finds it relaxing."
"But Tom says basketball is boring."
Miguel notes each opinion to improve classes.`,
      
      questions: [
        {
          type: "mc",
          q: "TEXT: 'I love swimming! It's so relaxing! 🏊' Does she like swimming?",
          options: ["Yes, she does", "No, she doesn't","yes, she is"],
          a: "Yes, she does",
          tip: "Love = likes a lot! ❤️",
          successTip: "Love + relaxing = very positive! She loves it! 💕"
        },
        {
          type: "mc",
          q: "TEXT: 'Basketball is boring.' 😴 Does he like basketball?",
          options: ["No, he doesn't", "Yes, he does","no, he isn't"],
          a: "No, he doesn't",
          tip: "Boring = negative opinion!",
          successTip: "Boring = doesn't like! Great inference! 🧠"
        },
        {
          type: "input",
          q: "TEXT: 'Tennis is exciting and fun!' What adjective describes tennis?",
          a: "exciting",
          alts: ["fun","great","amazing","cool","interesting","awesome","amazing","cool","interesting","awesome"],
          tip: "Two adjectives: exciting AND fun!",
          successTip: "Exciting OR fun! Both are correct answers! 🎾"
        },
        {
          type: "mc",
          q: "TEXT: 'I hate running because it makes me feel tired.' 😮‍💨 Why does she hate running?",
          options: ["It's tiring", "It's boring", "It's exciting"],
          a: "It's tiring",
          tip: "'Because' tells you the reason!",
          successTip: "Because it's tiring! You found the reason! 💡"
        },
        {
          type: "input",
          q: "TEXT: 'Surfing is dangerous but exciting!' Is surfing safe?",
          a: "No",
          alts: ["no it isn't", "dangerous","No it is not","No because it's dangerous"],
          tip: "Dangerous = NOT safe!",
          successTip: "No! Dangerous ≠ safe! Opposite meanings! 🏄⚠️"
        },
        {
          type: "mc",
          q: "TEXT: 'Golf is easy and relaxing.' How does he feel about golf?",
          options: ["He likes it", "He hates it"],
          a: "He likes it",
          tip: "Easy and relaxing = positive! ✅"
        },
        {
          type: "input",
          q: "TEXT: 'Soccer is my favorite sport! It's so fun!' ⚽ What's his favorite sport?",
          a: "soccer",
          alts: ["football"],
          tip: "Favorite sport = ?",
          successTip: "Soccer/Football! His favorite! ⚽"
        },
        {
          type: "mc",
          q: "TEXT: 'I'm not into hockey. It's too dangerous.' ⚠️ Does she like hockey?",
          options: ["No", "Yes"],
          a: "No",
          tip: "'Don't like' = negative!",
          successTip: "I'm not into something = I don't like it! ❌"
        },
        {
          type: "input",
          q: "TEXT: 'Baseball is boring and slow.' Does he like baseball?",
          a: "no",
          alts: ["not","No, he doesn't","Nope"],
          tip: "He says 'boring'!",
          successTip: "Boring! He gives his honest opinion! ⚾"
        },
        {
          type: "mc",
          q: "TEXT: 'Skiing is difficult but I love it! ⛷️' Does he like skiing?",
          options: ["Yes, even though it's difficult", "No, because it's difficult","He is not sure"],
          a: "Yes, even though it's difficult",
          tip: "Love it BUT difficult = ?",
          successTip: "even though significa aunque! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 6: INFERENCE & SYNONYMS (A2+)
    // ===================================================================
    {
      id: "m3-u4-set6",
      title: "Set 6: Understanding Opinions",
      context: `*Lunes siguiente - Análisis de opiniones*

Miguel analiza diferentes formas de expresar opiniones.
"Cuando alguien dice 'es divertido', significa que le gusta."
"Si dice 'es horrible', significa que lo odia."
Los estudiantes aprenden a interpretar opiniones.

*Next Monday - Opinion analysis*

Miguel analyzes different ways to express opinions.
"When someone says 'it's fun', it means they like it."
"If they say 'it's horrible', it means they hate it."
Students learn to interpret opinions.`,
      
      questions: [
        {
          type: "match",
          q: "Match the opinion to what it means.",
          pairs: [
            { left: "'It's fun!' 🎉", right: "I like it" },
            { left: "'It's boring.' 😴", right: "I don't like it" },
            { left: "'It's amazing!' 🤩", right: "I love it" }
          ],
          a: "completed",
          tip: "Positive adjectives = like! Negative = don't like!",
          successTip: "Positive adjectives = like! Negative = don't like! 🎯"
        },
        {
          type: "mc",
          q: "Miguel asks: 'If someone says Tennis is terrible, do they like tennis?'",
          options: ["No, they don't", "Yes, they do"],
          a: "No",
          tip: "Terrible = very negative! 👎",
          successTip: "Usa 'they' cuando hablas de una persona en general y desconoces su genero"
        },
        {
          type: "input",
          q: "Student: 'If someone says I love it!, do they like it?'",
          a: "Yes",
          alts: ["yes, they do","yes, they like it"],
          tip: "Love = strong positive!",
          successTip: "Love = REALLY like! ❤️"
        },
        {
          type: "match",
          q: "Match similar meanings.",
          pairs: [
            { left: "Lo odio", right: "I don't like it at all" },
            { left: "Me encanta", right: "I like it very much" },
            { left: "Me da igual", right: "I don't love it or hate it" }
          ],
          a: "completed",
          tip: "Different ways to express opinions!",
          successTip: "Different intensity! Hate < Don't like < OK < Like < Love! Scale! 📊"
        },
        {
          type: "mc",
          q: "TEXT: 'Swimming is horrible!' Does she like swimming?",
          options: ["No, she hates it", "Yes, she loves it"],
          a: "No, she hates it",
          tip: "Horrible = very negative = hates! 😡",
          successTip: "Horrible = hates! Strong negative! 💥"
        },
        {
          type: "input",
          q: "Miguel: 'If something is not boring, is it interesting?'",
          a: "Yes",
          alts: ["Yes, it is interesting","Yes, it's interesting","Yes, it is"],
          tip: "Not boring = interesting!",
          successTip: "Yes! Not boring = interesting! Opposite logic! 🔄"
        },
        {
          type: "mc",
          q: "TEXT: 'Basketball is fantastic!' How does he feel?",
          options: ["He really likes it", "He doesn't like it","He is not sure"],
          a: "He really likes it",
          tip: "Fantastic = great = loves it! ⭐"
        },
        {
          type: "match",
          q: "Match the feeling to the sport description.",
          pairs: [
            { left: "Loves the sport", right: "'It's exciting and amazing!'" },
            { left: "Hates the sport", right: "'It's boring and terrible!'" },
            { left: "Neutral", right: "'It's OK, I guess.'" }
          ],
          a: "completed",
          tip: "Positive/negative/neutral opinions!",
          successTip: "Learn to recognize feelings from adjectives! 🧠"
        },
        {
          type: "mc",
          q: "Miguel: 'If someone says It's not very exciting, do they love it?'",
          options: ["Probably not", "Yes, they love it","No, they hate it"],
          a: "Probably not",
          tip: "'Not very exciting' = mild negative!"
        },
        {
          type: "input",
          q: "Student: 'If someone says 'It's not safe', they think it's ___'",
          a: "risky",
          alts: ["dangerous", "not safe","unsafe"],
          tip: "Dangerous = risky = not safe! ⚠️",
          successTip: "Risky! Dangerous = risky = not safe! Synonyms! ⚠️"
        }
      ]
    },

    // ===================================================================
    // SET 7: SPORTS VENUES (A2)
    // ===================================================================
    {
      id: "m3-u4-set7",
      title: "Set 7: Where You Play 🏟️",
      context: `*Martes - Tour por las instalaciones*

Miguel da un tour por el centro deportivo.
"Aquí está la piscina para natación" señala.
"Las canchas de tenis están afuera. Y el campo de fútbol allá."
Los nuevos clientes toman fotos de cada área.

*Tuesday - Facilities tour*

Miguel gives a tour of the sports center.
"Here is the pool for swimming" he points out.
"The tennis courts are outside. And the soccer field is over there."
New clients take photos of each area.`,
      
      questions: [
        {
          type: "match",
          q: "Match the sport to where you play it.",
          pairs: [
            { left: "Swimming 🏊", right: "Pool" },
            { left: "Tennis 🎾", right: "Court" },
            { left: "Soccer ⚽", right: "Field / Pitch" }
          ],
          a: "completed",
          successTip: "Pool, court, field - learn where sports happen! 🏟️"
        },
        {
          type: "mc",
          q: "Miguel asks: 'Where do you play basketball?' 🏀",
          options: ["On a court", "In a stadium", "On a field"],
          a: "On a court",
          tip: "Court = cancha! 🏀"
        },
        {
          type: "input",
          q: "Client: 'You swim in a ___'",
          a: "pool",
          alts: ["swimming pool"],
          tip: "Swimming = pool!",
          successTip: "Pool! Swimming pool! 🏊"
        },
        {
          type: "match",
          q: "Match more venues.",
          pairs: [
            { left: "Golf ⛳", right: "Course" },
            { left: "Running 🏃", right: "Track / Street" },
            { left: "Ice hockey 🏒", right: "Ice rink" }
          ],
          a: "completed",
          tip: "Each sport has its special place!",
          successTip: "Course (golf), track (running), rink (ice hockey)! Specific places! 🏟️"
        },
        {
          type: "mc",
          q: "Miguel: 'Where do you play soccer?' ⚽",
          options: ["On a field", "On a court", "In a pool"],
          a: "On a field",
          tip: "Field (US) = Pitch (UK)! ⚽",
          successTip: "Field/Pitch! Both correct! Soccer field! ⚽"
        },
        {
          type: "input",
          q: "Client: 'You play tennis on a ___'",
          a: "court",
          alts: ["tennis court"],
          tip: "Tennis court! 🎾",
          successTip: "Court! Tennis court! 🎾"
        },
        {
          type: "mc",
          q: "Miguel shows: 'Where do people run races?' 🏃",
          options: ["On a track", "In a pool", "On a court"],
          a: "On a track",
          tip: "Track = pista! 🏃"
        },
        {
          type: "input",
          q: "Miguel: 'Big sports games happen in a ___'",
          a: "stadium",
          alts: ["venue", "big place", "big stadium","sports center","sports centre"],
          tip: "Big place = stadium!",
          successTip: "Stadium! Big place for big games! 🏟️"
        },
        {
          type: "match",
          q: "Match the activity to the place.",
          pairs: [
            { left: "Surfing 🏄", right: "Ocean / Beach" },
            { left: "Skiing ⛷️", right: "Mountain / Ski resort" },
            { left: "Skateboarding 🛹", right: "Skate park / Street" }
          ],
          a: "completed",
          tip: "Outdoor sports need nature!",
          successTip: "Outdoor sports! Ocean (surfing), mountain (skiing), street (skateboarding)! 🌊⛰️🛹"
        },
        {
          type: "mc",
          q: "Client asks: 'Where do you play golf?' ⛳",
          options: ["On a golf course", "In a stadium", "On a track"],
          a: "On a golf course",
          tip: "Golf course = campo de golf! ⛳",
          successTip: "Golf course! Large outdoor area with 18 holes! ⛳"
        }
      ]
    },

    // ===================================================================
    // SET 8: FREQUENCY + SPORTS (A2+)
    // ===================================================================
    {
      id: "m3-u4-set8",
      title: "Set 8: How Often? ⏰",
      context: `*Miércoles - Planificación de horarios*

Miguel ayuda a clientes a planificar sus rutinas deportivas.
"¿Con qué frecuencia quieres nadar? ¿Todos los días? ¿Dos veces por semana?"
Los clientes piensan en sus horarios y comprometen fechas.
Miguel anota todo en el calendario.

*Wednesday - Schedule planning*

Miguel helps clients plan their sports routines.
"How often do you want to swim? Every day? Twice a week?"
Clients think about their schedules and commit to dates.
Miguel writes everything on the calendar.`,
      
      questions: [
        {
          type: "match",
          q: "Match the frequency expression.",
          pairs: [
            { left: "Every day", right: "Daily / 7 times a week" },
            { left: "Twice a week", right: "2 times a week" },
            { left: "Once a month", right: "1 time a month" }
          ],
          a: "completed",
          tip: "Learn frequency expressions!",
          successTip: "Once, twice, three times - learn frequency! ⏰"
        },
        {
          type: "mc",
          q: "TEXT: 'I play tennis twice a week.' How often does he play?",
          options: ["2 times a week", "Every day", "Once a month"],
          a: "2 times a week",
          tip: "Twice = 2 times! 🎾"
        },
        {
          type: "input",
          q: "TEXT: 'She goes swimming every morning.' How often?",
          a: "every day",
          alts: ["daily", "every morning","always","everyday"],
          tip: "Every morning = ?",
          successTip: "Every morning = daily! 🏊"
        },
        {
          type: "order",
          q: "Traduce: 'Corro tres veces por semana.'",
          words: ["I", "go", "running", "three", "times", "a", "week."],
          a: "I go running three times a week.",
          extra: ["play", "twice", "month"],
          tip: "Number + times + a + period."
        },
        {
          type: "mc",
          q: "TEXT: 'We play soccer on weekends.' When do they play?",
          options: ["Saturday and Sunday", "Every day", "Once a month"],
          a: "Saturday and Sunday",
          tip: "Weekends = Saturday + Sunday! ⚽"
        },
        {
          type: "input",
          q: "Client: 'He plays basketball ___ a month. On the 1st'",
          a: "once",
          alts: ["one time"],
          tip: "Once = 1 time! 🏀",
          successTip: "Once! Once = 1, twice = 2, three times = 3! Pattern! 🔢"
        },
        {
          type: "order",
          q: "Traduce: 'Ella va al gimnasio todas las tardes.'",
          words: ["She", "goes", "to", "the", "gym", "every", "evening."],
          a: "She goes to the gym every evening.",
          extra: ["morning", "night"],
          tip: "Every + time period.",
          successTip: "Afternoon = 12 - 6pm!, evening = after 6pm (aprox) 🎯"
        },
        {
          type: "mc",
          q: "TEXT: 'I never play golf.' How often does he play?",
          options: ["0 times / Never", "Sometimes", "Always"],
          a: "0 times / Never",
          tip: "Never = 0 times! ⛳",
          successTip: "Never = absolutely not! 0! ❌"
        },
        {
          type: "input",
          q: "Miguel: 'They play ___ 🎾 four times a week.'",
          a: "tennis",
          alts: ["tenis"],
        },
        {
          type: "mc",
          q: "TEXT: 'I sometimes go swimming.' How often?",
          options: ["Not very often, but not never", "Every day", "Never"],
          a: "Not very often, but not never",
          tip: "Sometimes = occasionally!",
          successTip: "Sometimes = not always, not never! Middle frequency! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMPLEX READING (B1-)
    // ===================================================================
    {
      id: "m3-u4-set9",
      title: "Set 9: Reading Challenge 📖",
      context: `*Jueves - Leyendo historias de clientes*

Miguel lee historias completas que clientes escriben sobre deportes.
"Este cliente ama el tenis pero odia correr. Interesante."
"Esta clienta juega fútbol dos veces por semana aunque es peligroso."
Las historias ayudan a Miguel a entender mejor a cada persona.

*Thursday - Reading client stories*

Miguel reads complete stories that clients write about sports.
"This client loves tennis but hates running. Interesting."
"This client plays soccer twice a week even though it's dangerous."
The stories help Miguel understand each person better.`,
      
      questions: [
        {
          type: "mc",
          q: "TEXT: 'I love tennis because it's exciting, but I hate running because it's boring and tiring.' Which sport does she like?",
          options: ["Tennis", "Running", "Both"],
          a: "Tennis",
          tip: "Love = likes! Hate = doesn't like!",
          successTip: "Tennis! Love vs Hate - clear difference! 🎾"
        },
        {
          type: "mc",
          q: "Same text: Why does she hate running?",
          options: ["It's boring and tiring", "It's exciting", "It's easy"],
          a: "It's boring and tiring",
          tip: "'Because' tells you why!"
        },
        {
          type: "input",
          q: "Same text: Which sport is exciting?",
          a: "tennis",
          alts: ["tenis"],
          tip: "She says tennis is exciting!",
          successTip: "Tennis! She gives two reasons: exciting (positive) vs boring+tiring (negative)! 🎾"
        },
        {
          type: "mc",
          q: "TEXT: 'Soccer is fun but dangerous. I play it twice a week.' Does he like soccer?",
          options: ["Yes, but he knows it's dangerous", "No, he hates it"],
          a: "Yes, but he knows it's dangerous",
          tip: "Fun = positive! BUT shows contrast.",
          successTip: "Fun but dangerous = still likes it! BUT adds info! ⚽"
        },
        {
          type: "input",
          q: "Same text: How often does he play soccer?",
          a: "twice a week",
          alts: ["2 times a week", "twice"],
          tip: "How often = Que tan seguido!",
          successTip: "Twice a week! He plays regularly! ⚽📅"
        },
        {
          type: "mc",
          q: "TEXT: 'Basketball is my favorite sport! It's exciting and fast. I play it every day at the park.' How does he feel about basketball?",
          options: ["He loves it", "He hates it", "He doesn't care"],
          a: "He loves it",
          tip: "Favorite + exciting = loves!",
          successTip: "Favorite sport + every day = loves it! 🏀"
        },
        {
          type: "input",
          q: "Same text: Where does he play?",
          a: "park",
          alts: ["the park", "at the park"],
          tip: "'At the park'!",
          successTip: "Park! At the park! Outdoor basketball! 🏀🌳"
        },
        {
          type: "mc",
          q: "TEXT: 'I don't like swimming because it's boring, but my sister loves it because it's relaxing.' Who likes swimming?",
          options: ["His sister", "Him", "Both"],
          a: "His sister",
          tip: "Sister loves it, he doesn't like it!",
          successTip: "His sister! Different people, different opinions! 🏊"
        },
        {
          type: "input",
          q: "Same text: Why does his sister love swimming?",
          a: "relaxing",
          alts: ["it's relaxing"],
          tip: "'Because it's relaxing'!",
          successTip: "Relaxing! Different people, different opinions! 🏊"
        },
        {
          type: "mc",
          q: "TEXT: 'Golf is easy but boring. I play it once a month with my dad.' Does he enjoy golf?",
          options: ["Not really (boring)", "Yes, he loves it"],
          a: "Not really (boring)",
          tip: "Boring = negative! Once a month = not often.",
          successTip: "Boring + once a month = doesn't enjoy it much! Great inference! 🧠"
        }
      ]
    },

    // ===================================================================
    // SET 10: REAL SPORTS TALK (B1-)
    // ===================================================================
    {
      id: "m3-u4-set10",
      title: "Set 10: Sports Conversations 💬",
      context: `*Viernes - Conversaciones reales*

Miguel practica conversaciones reales sobre deportes con clientes.
"¿Cuál es tu deporte favorito? ¿Por qué te gusta? ¿Con qué frecuencia lo practicas?"
Los clientes responden con frases completas y naturales.
Es el momento de usar todo lo aprendido.

*Friday - Real conversations*

Miguel practices real conversations about sports with clients.
"What's your favorite sport? Why do you like it? How often do you play it?"
Clients respond with complete and natural phrases.
It's time to use everything learned.`,
      
      questions: [
        {
          type: "match",
          q: "Match the question to the appropriate answer.",
          pairs: [
            { left: "Do you like soccer?", right: "Yes, I love it!" },
            { left: "Why do you like tennis?", right: "Because it's exciting!" },
            { left: "How often do you swim?", right: "Twice a week." }
          ],
          a: "completed",
          tip: "Natural conversation patterns!",
          successTip: "Real conversation patterns! Use them! 💬"
        },
        {
          type: "order",
          q: "Traduce: 'No me gusta el baloncesto porque es cansado.'",
          words: ["I", "don't", "like", "basketball", "because", "it's", "tiring."],
          a: "I don't like basketball because it's tiring.",
          extra: ["hate", "boring", "play"],
          tip: "Opinion + because + reason."
        },
        {
          type: "mc",
          q: "A: 'What's your favorite sport?' B: '___'",
          options: ["Tennis! I love it!", "I go swimming.", "On weekends."],
          a: "Tennis! I love it!",
          tip: "Favorite = which one you like most!",
          successTip: "Perfect answer! Name the sport + opinion! 🎾"
        },
        {
          type: "input",
          q: "Complete: 'I play ____ every day.'",
          a: "football",
          alts: ["soccer", "tennis","basketball","baseball","rugby","volleyball"],
          successTip: "Every day = todos los días (frecuencia), Everyday = cotidiano / común (adjetivo)"
        },
        {
          type: "order",
          q: "Traduce: '¿Por qué te gusta nadar?'",
          words: ["Why", "do", "you", "like", "swimming?"],
          a: "Why do you like swimming?",
          extra: ["play", "go", "are"],
          tip: "Why = asking for reason."
        },
        {
          type: "mc",
          q: "A: 'Do you play any sports?' B: '___'",
          options: ["Yes, I play tennis twice a week.", "No, I don't like them.", "Both A and B are correct"],
          a: "Both A and B are correct",
          tip: "Both answers work!",
          successTip: "Both work! You can say yes with details OR just no! 💡"
        },
        {
          type: "mc",
          q: "A: 'Is skiing dangerous?' B: 'Yes, but ___'",
          options: ["it isn't safe", "it's risky", "it's exciting"],
          a: "it's exciting",
          tip: "BUT shows contrast - dangerous BUT still good!",
          successTip: "It's exciting! Dangerous BUT exciting = contrast! Still likes it! ⛷️"
        },
        {
          type: "order",
          q: "Traduce: 'Me encanta surfear porque es emocionante!'",
          words: ["I", "love", "surfing", "because", "it's", "exciting!"],
          a: "I love surfing because it's exciting!",
          extra: ["play", "go", "boring"],
          tip: "Opinion + sport + because + adjective.",
          successTip: "Perfect structure! Use this pattern always! 🏄"
        },
        {
          type: "mc",
          q: "A: 'What do you think about golf?' B: '___'",
          options: ["I think it's boring.", "I go it on Sundays.", "I don't wear it."],
          a: "I think it's boring.",
          tip: "wear is for clothes and I go it is not correct.",
        },
        {
          type: "input",
          q: "Final challenge! What's YOUR favorite sport? Write it!",
          a: "soccer",
          alts: ["basketball", "tennis", "swimming", "running", "baseball", "football", "volleyball", "hockey", "golf", "skiing", "surfing", "cycling", "skateboarding", "boxing", "none", "I don't have one"],
          tip: "Any sport is correct! Or 'none'!",
          successTip: "Awesome! Now you can talk about YOUR sports! Unit 4 complete! 🏆"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Sports in My Life (Activities & Opinions)
  // ===================================================================
  readingSet: {
    id: "m3-u4-reading",
    title: "Reading: Sports in My Life 🏃⚽",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**The Beautiful Game**

Hi! My name is Alex and I love sports! My favorite sport is soccer. I play it three times a week with my friends at the park. Soccer is exciting and fun! I love it because it's a team sport and I can play with my friends. Sometimes it's tiring, but that's OK!`
      },
      {
        type: "mc",
        q: "What is Alex's favorite sport?",
        options: ["Soccer", "Swimming", "Basketball"],
        a: "Soccer",
        tip: "Check the second sentence: 'My favorite sport is...'",
        successTip: "✓ GOAL!\nSoccer is Alex's number one passion! ⚽"
      },
      {
        type: "input",
        q: "How often does Alex play soccer?",
        a: "three times a week",
        alts: ["3 times a week", "three times"],
        tip: "Look for the frequency expression in the first paragraph.",
        successTip: "✓ FREQUENCY!\nPlaying 3 times a week is great exercise! 🗓️"
      },
      {
        type: "mc",
        q: "Why does Alex love soccer?",
        options: ["It's a team sport and fun", "It's easy", "It's relaxing"],
        a: "It's a team sport and fun",
        tip: "Look for 'because' in the text.",
        successTip: "✓ TEAMWORK!\nPlaying with friends makes the sport even better! 🤝"
      },
      {
        type: "passage",
        passage: `**Pool and Court**

I also go swimming twice a week at the community pool. Swimming is relaxing and easy for me. I'm good at it! I learned when I was 5 years old.

I don't like basketball very much. I think it's boring because I'm not very good at it. My brother loves basketball, but I prefer soccer.`
      },
      {
        type: "input",
        q: "Where does Alex go swimming?",
        a: "community pool",
        alts: ["pool", "the pool"],
        tip: "Check the first sentence of this block.",
        successTip: "✓ SPLASH!\nHe uses the neighborhood facilities for his laps. 🏊‍♂️"
      },
      {
        type: "mc",
        q: "Does Alex like basketball?",
        options: ["No, he doesn't", "Yes, he loves it"],
        a: "No, he doesn't",
        tip: "Look at his opinion: 'I don't like...'",
        successTip: "✓ NOT FOR HIM!\nHe thinks it's boring because he's not very skilled at it. 🏀"
      },

    {
        type: "passage",
        passage: `**Rackets and Wheels**

I tried tennis once with my dad. It was difficult! You need a racket and you play on a court. My dad is really good at tennis, but I'm not. Maybe I'll try again next month.

On weekends, I sometimes go cycling with my family. We ride our bicycles in the park. Cycling is fun and it's good exercise!`
      },
      {
        type: "input",
        q: "What equipment do you need for tennis?",
        a: "racket",
        alts: ["a racket", "tennis racket"],
        tip: "Check the sentence starting with 'You need a...'",
        successTip: "✓ CORRECT!\nYou can't hit the ball without a racket! 🎾"
      },
      {
        type: "mc",
        q: "Is Alex good at tennis?",
        options: ["No, he's not", "Yes, he is"],
        a: "No, he's not",
        tip: "Read the comparison between Alex and his dad.",
        successTip: "✓ LEARNER!\nHe found it difficult, but he might try again! 👟"
      },
      {
        type: "input",
        q: "What does Alex do with his family on weekends?",
        a: "cycling",
        alts: ["ride bicycles", "bike"],
        tip: "Last paragraph: 'On weekends, I...'",
        successTip: "✓ CYCLING!\nA great way to spend time with family in the park. 🚴"
      },
  {
        type: "passage",
        passage: `**The Final Verdict**

My least favorite sport is golf. I think it's very boring and slow. My grandfather plays golf every Sunday, but I never play it. Sports are great! They're good for your health and they're fun!`
      },
      {
        type: "mc",
        q: "What does Alex think about golf?",
        options: ["It's boring and slow", "It's exciting", "It's fun"],
        a: "It's boring and slow",
        tip: "Look for his specific adjective about golf.",
        successTip: "✓ SLOW PACE!\nNot everyone has the patience for golf! ⛳"
      },
      {
        type: "mc",
        q: "How often does Alex play golf?",
        options: ["Never", "Once a week", "Every Sunday"],
        a: "Never",
        tip: "Check the frequency word he uses for golf.",
        successTip: "✓ ZERO PERCENT!\nHe leaves the golfing to his grandfather. 🏆"
      }
    ]
  },

  reviewSet: {
    id: "m3-u4-review",
    title: "Unit 4 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: " You ___ swimming.",
        options: ["go", "play"],
        a: "go",
        tip: "Swimming = GO + -ing!"
      },
      {
        type: "input",
        q: "They ___ soccer twice a week.",
        a: "play",
        alts: [],
        tip: "Soccer = PLAY (team sport)!"
      },
      {
        type: "mc",
        q: " What do you need for tennis?",
        options: ["A racket", "A bat", "A club"],
        a: "A racket",
        tip: "Tennis = racket!"
      },
      {
        type: "input",
        q: "If a sport makes you tired, it's ___.",
        a: "tiring",
        alts: [],
        tip: "Makes you tired = tiring!"
      },
      {
        type: "mc",
        q: "Where do you swim?",
        options: ["In a pool", "On a court", "On a field"],
        a: "In a pool",
        tip: "Swimming = pool!"
      },
      {
        type: "order",
        q: "Traduce: 'Juego baloncesto todos los días.'",
        words: ["I", "play", "basketball", "every", "day."],
        a: "I play basketball every day.",
        extra: ["go", "tennis", "week"],
        tip: "Basketball = PLAY (not GO)!"
      },
      {
        type: "input",
        q: "'I play tennis ___ a week.' (2 times)",
        a: "twice",
        alts: ["two times"],
        tip: "2 times = twice!"
      },
      {
        type: "mc",
        q: "If something is 'emocionante', it's ___.",
        options: ["exciting", "boring", "tiring"],
        a: "exciting",
        tip: "Emocionante = exciting!"
      },
      {
        type: "input",
        q: "The opposite of 'safe' is ___.",
        a: "dangerous",
        alts: ["not safe","danger","unsafe","risky","hazardous"],
        tip: "Safe ↔ Dangerous!",
        successTip: "Dangerous! Safe ↔ Dangerous! Learn opposites! 🔄"
      },
      {
        type: "mc",
        q: "TEXT: 'I love running because it's fun!' Does he like running?",
        options: ["Yes, he does", "No, he doesn't", "Yes, he is", "No, he isn't"],
        a: "Yes",
        tip: "Love = likes a lot!",
        successTip: "🏆 UNIT 4 REVIEW COMPLETE! You can now talk about sports, use GO vs PLAY, describe sports, and express opinions! Ready for Unit 5! 🚀"
      }
    ]
  }
};
